import type { PayloadAction } from "@reduxjs/toolkit"
import { createAppSlice } from "../../app/createAppSlice"

import type { GameLogicModifiers, GameLogicState, SlideGeneric } from "@types"
import defaultGameData from "../default"

// Load default from JSON
export const gameInitialState: GameLogicState = defaultGameData.start

const START_SLIDE_TITLE = defaultGameData.start.slide?.title?.en

export type ApplyTurnPayload = {
  /** Game state after the choice (from planTurn / processTurn) */
  updated: GameLogicState
  /** The slide to show next */
  slide: SlideGeneric
  /** The choice's effects, for the meter feedback */
  effects?: GameLogicModifiers
}

export const gameSlice = createAppSlice({
  name: "game",
  initialState: gameInitialState,
  reducers: create => ({
    appendHistory: create.reducer(
      (state, action: PayloadAction<GameLogicState["history"][0]>) => {
        state.history.push(action.payload)
      },
    ),
    incrementGameTurn: create.reducer(state => {
      state.turn += 1
    }),
    resetGame: create.reducer(() => gameInitialState),
    updateGame: create.reducer(
      (state, action: PayloadAction<GameLogicModifiers>) => {
        state.age += action.payload?.age ?? 0
        state.agency += action.payload?.agency ?? 0
        state.education += action.payload?.education ?? 0
        state.health += action.payload?.health ?? 0
        state.relationship += action.payload?.relationship ?? 0
        state.mentalHealth += action.payload?.mentalHealth ?? 0
        state.physicalHealth += action.payload?.physicalHealth ?? 0
      },
    ),
    updateDuration: create.reducer((state, action: PayloadAction<number>) => {
      state.duration += action.payload
    }),
    updateGameSlide: create.reducer(
      (state, action: PayloadAction<SlideGeneric>) => {
        state.slide = action.payload
      },
    ),
    updateAddGameTags: create.reducer(
      (state, action: PayloadAction<string>) => {
        if (!state.tags.includes(action.payload)) {
          state.tags.push(action.payload)
        }
      },
    ),
    updateRemoveGameTags: create.reducer(
      (state, action: PayloadAction<string>) => {
        if (state.tags.includes(action.payload)) {
          state.tags = state.tags.filter(tag => tag !== action.payload)
        }
      },
    ),
    // One commit per choice: meters, tags, history, turn, the next slide and
    // the feedback effects land together, so the UI renders the new turn once.
    applyTurn: create.reducer(
      (state, action: PayloadAction<ApplyTurnPayload>) => {
        const { updated, slide, effects } = action.payload
        state.age = updated.age
        state.agency = updated.agency
        state.education = updated.education
        state.health = updated.health
        state.relationship = updated.relationship
        state.mentalHealth = updated.mentalHealth
        state.physicalHealth = updated.physicalHealth
        state.tags = updated.tags
        state.history = updated.history
        // processTurn already advanced the turn; do not increment again
        state.turn = updated.turn
        state.slide = slide
        state.lastTurn = { nonce: updated.turn, effects: effects ?? {} }
      },
    ),
    // New setGame reducer that handles all updates from processTurn
    setGame: create.reducer(
      (state, action: PayloadAction<Partial<GameLogicState>>) => {
        // Update all game state properties
        if (action.payload.age !== undefined) state.age = action.payload.age
        if (action.payload.agency !== undefined)
          state.agency = action.payload.agency
        if (action.payload.education !== undefined)
          state.education = action.payload.education
        if (action.payload.health !== undefined)
          state.health = action.payload.health
        if (action.payload.relationship !== undefined)
          state.relationship = action.payload.relationship
        if (action.payload.mentalHealth !== undefined)
          state.mentalHealth = action.payload.mentalHealth
        if (action.payload.physicalHealth !== undefined)
          state.physicalHealth = action.payload.physicalHealth
        if (action.payload.turn !== undefined) state.turn = action.payload.turn
        if (action.payload.duration !== undefined)
          state.duration = action.payload.duration
        if (action.payload.isDone !== undefined)
          state.isDone = action.payload.isDone

        // Update tags if provided
        if (action.payload.tags !== undefined) {
          state.tags = action.payload.tags
        }

        // Update history if provided
        if (action.payload.history !== undefined) {
          state.history = action.payload.history
        }

        // Update slide if provided
        if (action.payload.slide !== undefined) {
          state.slide = action.payload.slide
        }
      },
    ),
  }),
  // You can define your selectors here. These selectors receive the slice
  // state as their first argument.
  selectors: {
    // State
    selectIsDone: state => state?.isDone ?? false,
    selectGameAge: state => state.age,
    selectGameAgency: state => state.agency,
    selectGameEducation: state => state.education,
    selectGameHealth: state => state.health,
    selectGameRelationship: state => state.relationship,
    selectGameTags: state => state.tags,
    selectGameMentalHealth: state => state.mentalHealth,
    selectGamePhysicalHealth: state => state.physicalHealth,
    // Slide
    selectSlide: state => state.slide,
    // Statistics
    selectDuration: state => state.duration,
    selectHistory: state => state.history,
    selectTurn: state => state.turn,
    selectScore: state =>
      state.agency +
      state.education +
      state.health +
      state.relationship +
      state.mentalHealth +
      state.physicalHealth,
    // Booleans for the page shell, so it does not re-render every turn
    selectHasGameStarted: state => state.turn > 0 || state.history.length > 0,
    selectIsOnStartSlide: state =>
      !state.slide || state.slide.title?.en === START_SLIDE_TITLE,
    // Feedback for the most recent choice (primitives per meter)
    selectLastTurnNonce: state => state.lastTurn?.nonce ?? 0,
    selectLastTurnEffects: state => state.lastTurn?.effects,
    selectLastTurnAgencyEffect: state => state.lastTurn?.effects?.agency ?? 0,
    selectLastTurnEducationEffect: state =>
      state.lastTurn?.effects?.education ?? 0,
    selectLastTurnHealthEffect: state => state.lastTurn?.effects?.health ?? 0,
    selectLastTurnRelationshipEffect: state =>
      state.lastTurn?.effects?.relationship ?? 0,
    selectLastTurnMentalHealthEffect: state =>
      state.lastTurn?.effects?.mentalHealth ?? 0,
    selectLastTurnPhysicalHealthEffect: state =>
      state.lastTurn?.effects?.physicalHealth ?? 0,
  },
})

export const {
  appendHistory,
  incrementGameTurn,
  updateGame,
  updateGameSlide,
  updateAddGameTags,
  updateRemoveGameTags,
  resetGame,
  setGame,
  applyTurn,
} = gameSlice.actions

export const {
  selectIsDone,
  selectGameAge,
  selectGameAgency,
  selectGameEducation,
  selectGameHealth,
  selectGameRelationship,
  selectGameTags,
  selectGameMentalHealth,
  selectGamePhysicalHealth,
  // Slide
  selectSlide,
  // Statistics
  selectDuration,
  selectHistory,
  selectTurn,
  selectScore,
  selectHasGameStarted,
  selectIsOnStartSlide,
  selectLastTurnNonce,
  selectLastTurnEffects,
  selectLastTurnAgencyEffect,
  selectLastTurnEducationEffect,
  selectLastTurnHealthEffect,
  selectLastTurnRelationshipEffect,
  selectLastTurnMentalHealthEffect,
  selectLastTurnPhysicalHealthEffect,
} = gameSlice.selectors
